import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  User
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { Service } from '../shared/hooks/useServiceSelection';

interface ProviderType {
  firstName: string;
  lastName: string;
}

interface SelectionHeaderProps {
  selectedServices: Service[];
  serviceProviderMap: Record<string, ProviderType>;
  activeService: string;
  setActiveService: (id: string) => void;
}

const ServiceOverview: React.FC<SelectionHeaderProps> = ({
  selectedServices,
  serviceProviderMap,
  activeService,
  setActiveService,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const [currentPage, setCurrentPage] = useState(0);
  const servicesPerPage = 4;
  const totalPages = Math.ceil(selectedServices.length / servicesPerPage);

  const startIndex = currentPage * servicesPerPage;
  const visibleServices = selectedServices.slice(startIndex, startIndex + servicesPerPage);

  const handlePageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    } else if (direction === 'next' && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getProviderDisplayText = (providerId: string, provider: ProviderType | { autoAssigned: boolean } | undefined) => {
    if (!provider) return t('providers.notSelected'); // CHANGED: 'Provider not selected'
    if ('autoAssigned' in provider && provider.autoAssigned) return t('providers.willBeAssigned'); // CHANGED: 'Provider will be assigned'
    if ('firstName' in provider && 'lastName' in provider) {
      return `${provider.firstName} ${provider.lastName}`;
    }
    return t('providers.notSelected'); // CHANGED: 'Provider not selected'
  };

  const isValidProvider = (provider: any) => {
    return provider && (
      ('firstName' in provider && 'lastName' in provider) ||
      ('autoAssigned' in provider && provider.autoAssigned)
    );
  };

  return (
    <div className="lp-flex-1 md:lp-border-l md:lp-pl-6 lp-border-[#DDB889]/10 lp-mt-6 md:lp-mt-0 lp-pt-6 md:lp-pt-0 lp-border-t md:lp-border-t-0">
      <div className="lp-flex lp-items-center lp-justify-between lp-mb-3">
        {selectedServices.length > servicesPerPage ? (
          <motion.button
            onClick={() => handlePageChange('prev')}
            disabled={currentPage === 0}
            className={`lp-p-1.5 lp-text-[#DDB889] lp-border lp-border-[#DDB889]/10 lp-rounded-full ${
              currentPage === 0
                ? 'lp-opacity-50 lp-cursor-not-allowed'
                : 'hover:lp-bg-[#DDB889] hover:lp-text-black lp-transition-colors'
            }`}
            whileHover={currentPage > 0 ? { scale: 1.05 } : {}}
            whileTap={currentPage > 0 ? { scale: 0.95 } : {}}
          >
            <ChevronLeft className="lp-h-4 lp-w-4" />
          </motion.button>
        ) : (
          <div className="lp-w-7" />
        )}

        <h3 className="lp-text-[#DDB889] lp-flex lp-items-center lp-gap-2">
          <Calendar className="lp-w-4 lp-h-4" />
          {t('providers.selectedServicesOverview')} {/* CHANGED: 'Selected Services Overview' */}
        </h3>

        {selectedServices.length > servicesPerPage ? (
          <motion.button
            onClick={() => handlePageChange('next')}
            disabled={currentPage === totalPages - 1}
            className={`lp-p-1.5 lp-text-[#DDB889] lp-border lp-border-[#DDB889]/10 lp-rounded-full ${
              currentPage === totalPages - 1
                ? 'lp-opacity-50 lp-cursor-not-allowed'
                : 'hover:lp-bg-[#DDB889] hover:lp-text-black lp-transition-colors'
            }`}
            whileHover={currentPage < totalPages - 1 ? { scale: 1.05 } : {}}
            whileTap={currentPage < totalPages - 1 ? { scale: 0.95 } : {}}
          >
            <ChevronRight className="lp-h-4 lp-w-4" />
          </motion.button>
        ) : (
          <div className="lp-w-7" />
        )}
      </div>

      <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-2 lp-gap-3">
        {visibleServices.map((service) => {
          const isSelected = activeService === service.id;
          const provider = serviceProviderMap[service.id];
          const hasProvider = isValidProvider(provider);
          const providerText = getProviderDisplayText(service.id, provider);

          return (
            <motion.button
              key={service.id}
              onClick={() => setActiveService(service.id)}
              className={`
                lp-relative lp-p-3 lp-rounded-xl lp-transition-all lp-duration-200
                lp-flex lp-items-center lp-justify-between
                lp-border
                ${hasProvider ? 'lp-border-green-500/30' : 'lp-border-red-500/30'}
                ${isSelected
                  ? 'lp-bg-[#DDB889]/5 before:lp-absolute before:lp-inset-0 before:lp-bg-gradient-to-r before:lp-from-[#DDB889]/0 before:lp-to-[#DDB889]/5 before:lp-rounded-xl'
                  : 'lp-bg-black/95 hover:lp-bg-[#DDB889]/5'}
              `}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="lp-relative lp-flex lp-items-center lp-gap-3 lp-min-w-0">
                {hasProvider ? (
                  <CheckCircle2 className="lp-w-5 lp-h-5 lp-flex-shrink-0 lp-text-green-500" />
                ) : (
                  <AlertCircle className="lp-w-5 lp-h-5 lp-flex-shrink-0 lp-text-red-500" />
                )}
                <div className="lp-text-left lp-min-w-0">
                  <p className={`lp-font-medium lp-truncate lp-max-w-[180px] ${
                    isSelected ? 'lp-text-[#DDB889]' : 'lp-text-gray-200'
                  }`} title={service.name}>
                    {service.name}
                  </p>
                  <p className="lp-text-sm lp-truncate lp-max-w-[180px] lp-text-gray-400"
                     title={providerText}>
                    {providerText}
                  </p>
                </div>
              </div>
              <ArrowRight className={`lp-relative lp-w-4 lp-h-4 lp-flex-shrink-0 lp-ml-2
                ${isSelected ? 'lp-text-[#DDB889]' : 'lp-text-gray-400'}`}
              />
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

const SelectionHeader: React.FC<SelectionHeaderProps> = ({
  selectedServices,
  serviceProviderMap,
  activeService,
  setActiveService,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-bg-black/95 lp-backdrop-blur-sm lp-rounded-2xl lp-p-6
        lp-border lp-border-[#DDB889]/10 lp-min-h-[245px]
        lp-shadow-[0_0_15px_rgba(221,184,137,0.07)]
        hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]
        lp-transition-all lp-duration-700"
    >
      <div className="lp-flex lp-flex-col md:lp-flex-row lp-justify-between lp-gap-6">
        {/* Left Column - Healthcare Progress Overview */}
        <div className="lp-flex-1">
          <div className="lp-flex lp-items-center lp-gap-3 lp-mb-4">
            <div className="lp-bg-[#DDB889] lp-rounded-full lp-p-2">
              <User className="lp-w-5 lp-h-5 lp-text-black" />
            </div>
            <h2 className="lp-text-xl lp-font-medium lp-text-[#DDB889]">
              {t('providers.providerSelection')} {/* CHANGED: 'Provider Selection' */}
            </h2>
          </div>
          <p className="lp-text-gray-400 lp-text-sm lp-mb-4">
            {t('providers.followTheseSteps')} {/* CHANGED: 'Follow these steps to select your healthcare providers:' */}
          </p>
          <div className="lp-space-y-3">
            <div className="lp-flex lp-items-center lp-gap-3">
              <div className="lp-w-6 lp-h-6 lp-rounded-full lp-bg-green-500/20 lp-flex lp-items-center lp-justify-center">
                <CheckCircle2 className="lp-w-4 lp-h-4 lp-text-green-500" />
              </div>
              <span className="lp-text-gray-300">
                {t('providers.servicesSelected')}: {selectedServices?.length} {/* CHANGED: 'Services Selected:' */}
              </span>
            </div>
            <div className="lp-flex lp-items-center lp-gap-3">
              <div className="lp-w-6 lp-h-6 lp-rounded-full lp-bg-[#DDB889]/20 lp-flex lp-items-center lp-justify-center">
                <Clock className="lp-w-4 lp-h-4 lp-text-[#DDB889]" />
              </div>
              <span className="lp-text-gray-300">
                {t('providers.providersSelected')}: {Object.keys(serviceProviderMap).length}/{selectedServices?.length} {/* CHANGED: 'Providers Selected:' */}
              </span>
            </div>
          </div>
        </div>

        {/* Right Column - Service Overview */}
        <ServiceOverview
          selectedServices={selectedServices}
          serviceProviderMap={serviceProviderMap}
          activeService={activeService}
          setActiveService={setActiveService}
        />
      </div>
    </motion.div>
  );
};

export default SelectionHeader;
